import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AppLayout } from "../components/layout";
import { Button, Card, CardContent } from "@/components/ui";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { 
  Save,
  Eye,
  AlertCircle,
  CheckCircle,
  User,
  RotateCcw,
  ArrowLeft,
  Upload,
  Copy,
  History,
  Send,
  HelpCircle,
  Search,
  ChevronDown,
  ChevronRight,
  Sparkles
} from "lucide-react";
import { getHelpCenter, getItem, updateItem, getHelpCenterStructure, importDocument, publishItem, getArticlePreviewToken } from "../services/api";
import type { UpdateItemRequest } from "../types";
import type { Article, ArticleRevisionFull } from "../types";
import { ArticleVersionHistory } from "@/components/features/help-center/ArticleVersionHistory";
import { PublishingHelpModal } from "@/components/features/help-center/PublishingHelpModal";
import { useUIStore } from "../store/uiStore";
import { useIsProCloudPlan } from "../store/userStore";
import { usePenpal } from "../hooks/usePenpal";
import { getHelpCenterBaseUrl } from "../lib/utils";
import { IconPicker } from "@/components/features/help-center/IconPicker";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "react-hot-toast";
import { TipTapEditor } from "@/components/editor/TipTapEditor";

// Find an item's full nested path (e.g. "/category/subcategory/article-slug") in the
// help-center structure tree, so public/preview URLs reflect the real hierarchy.
const findItemPath = (nodes: any[], targetItemId: string): string | null => {
  for (const node of nodes || []) {
    if (node?.type === 'item' && node.item && String(node.item.id) === String(targetItemId) && node.path) {
      return node.path;
    }
    if (node?.children?.length) {
      const found = findItemPath(node.children, targetItemId);
      if (found) return found;
    }
  }
  return null;
};

const EditArticlePage: React.FC = () => {
  const { itemId, helpCenterId: helpCenterIdParam } = useParams<{ itemId: string; helpCenterId: string }>();
  const navigate = useNavigate();
  const { setIsSaving, lockNavigation, unlockNavigation, openModal } = useUIStore();
  const isProCloudPlan = useIsProCloudPlan();
  const { openExternalUrl, isInIframe } = usePenpal();
  

  
  const [article, setArticle] = useState<Article | null>(null);
  const [item, setItem] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [helpCenterInfo, setHelpCenterInfo] = useState<{ id: number; title: string; slug?: string } | null>(null);
  const [helpCenterStructure, setHelpCenterStructure] = useState<any[]>([]);
  
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [content, setContent] = useState<any>(null);
  const [contentHtml, setContentHtml] = useState<string>('');
  const [contentText, setContentText] = useState('');
  const [icon, setIcon] = useState('');
  const [showAuthor, setShowAuthor] = useState(true);
  // Per-article SEO (stored in article settings; empty overrides fall back to title/description)
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [noindex, setNoindex] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showPublishingHelp, setShowPublishingHelp] = useState(false);
  const [showSeo, setShowSeo] = useState(false);
  // Draft / Publish state (only relevant for draft-mode articles)
  const [usesDrafts, setUsesDrafts] = useState(false);
  const [isPublishedContent, setIsPublishedContent] = useState(true);
  const [hasServerDraft, setHasServerDraft] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Navigation lock state
  const initialFormState = useRef<{
    title: string;
    description: string;
    slug: string;
    contentText: string;
    contentHtml: string;
    icon: string;
    showAuthor: boolean;
    seoTitle: string;
    seoDescription: string;
    noindex: boolean;
    isPublished: boolean;
  } | null>(null);

    // Help center ID is required - items always belong to a help center
  if (!helpCenterIdParam || !itemId) {
    return <div>Missing required parameters</div>;
  }
  
  // Build breadcrumbs
  const breadcrumbs = (() => {
    const crumbs = [
      { title: 'Dashboard', href: 'dashboard' },
      { title: 'Help Centers', href: 'help-centers' },
    ] as any;
    
    if (helpCenterInfo) {
      crumbs.push(
        { title: helpCenterInfo.title, href: `help-centers/${helpCenterInfo.id}/structure` },
        { title: 'Edit Article', isCurrentPage: true }
      );
    } else {
      crumbs.push({ title: 'Edit Article', isCurrentPage: true });
    }
    
    return crumbs;
  })();

  // Generate slug from title
  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Check if form has been modified
  const checkForChanges = () => {
    if (!initialFormState.current) return false;
    
    const current = {
      title,
      description,
      slug,
      contentText,
      contentHtml,
      icon,
      showAuthor,
      seoTitle,
      seoDescription,
      noindex,
      isPublished
    };
    
    return JSON.stringify(current) !== JSON.stringify(initialFormState.current);
  };

  // Handle discard changes
  const handleDiscardChanges = () => {
    if (!initialFormState.current) return;
    
    if (!confirm('Are you sure you want to discard all changes? This action cannot be undone.')) {
      return;
    }

    // Reset to initial state
    setTitle(initialFormState.current.title);
    setDescription(initialFormState.current.description);
    setSlug(initialFormState.current.slug);
    setSlugManuallyEdited(true); // Preserve manual edit state when discarding
    setContentText(initialFormState.current.contentText);
    setContentHtml(initialFormState.current.contentHtml);
    setIcon(initialFormState.current.icon);
    setShowAuthor(initialFormState.current.showAuthor);
    setSeoTitle(initialFormState.current.seoTitle);
    setSeoDescription(initialFormState.current.seoDescription);
    setNoindex(initialFormState.current.noindex);
    setIsPublished(initialFormState.current.isPublished);
    
    // If we have the article data, reset content too
    if (article && article.content) {
      setContent(article.content);
    } else {
      setContent(null);
    }
    
    toast.success('Changes discarded');
  };

  // Update navigation lock based on changes
  useEffect(() => {
    // Don't lock navigation until initial state is set (data has loaded)
    if (!initialFormState.current) {
      unlockNavigation();
      return;
    }
    
    const hasChanges = checkForChanges();
    
    if (hasChanges) {
      lockNavigation("You have unsaved changes in your article");
    } else {
      unlockNavigation();
    }
  }, [title, description, slug, contentText, contentHtml, icon, showAuthor, seoTitle, seoDescription, noindex, isPublished, lockNavigation, unlockNavigation]);

  // Handle browser beforeunload event
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Only show warning if initial state is set and there are actual changes
      if (initialFormState.current && checkForChanges()) {
        e.preventDefault();
        e.returnValue = "You have unsaved changes. Are you sure you want to leave?";
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [title, description, slug, contentText, contentHtml, icon, showAuthor, seoTitle, seoDescription, noindex, isPublished]);

  // Fetch help center info and item data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch help center info
        const helpCenterResponse = await getHelpCenter(helpCenterIdParam);
        if (helpCenterResponse.ok && helpCenterResponse.helpCenter) {
          const helpCenter = helpCenterResponse.helpCenter;
          setHelpCenterInfo({
            id: helpCenter.id,
            title: helpCenter.title,
            slug: helpCenter.slug,
          });
          
          // Fetch help center structure for reference blocks
          const structureResponse = await getHelpCenterStructure(helpCenterIdParam);
          if (structureResponse.ok && structureResponse.structure) {
            setHelpCenterStructure(structureResponse.structure);
          }
        }

        // Fetch item data (which includes article or guide)
        const itemResponse = await getItem(helpCenterIdParam, itemId);
        if (itemResponse.ok && itemResponse.item) {
          const itemData = itemResponse.item;

          // Redirect to guide editor if this is a guide
          if (itemData.item_type === 'guide') {
            navigate(`/help-centers/${helpCenterIdParam}/items/${itemId}/edit-guide`, { replace: true });
            return;
          }

          setItem(itemData);

          if (itemData.item_type === 'article' && itemData.article) {
            const articleData = itemData.article;
            setArticle(articleData);

            // Draft/publish state
            setUsesDrafts(!!articleData.uses_drafts);
            setIsPublishedContent(!!articleData.is_published_content);
            setHasServerDraft(!!articleData.has_draft);

            // If a draft exists, edit the DRAFT content; otherwise the published content.
            const src = articleData.draft ? articleData.draft : articleData;

            setTitle(src.title || '');
            setDescription(src.description || '');
            setSlug(src.slug || articleData.slug || '');
            // Keep the slug auto-synced with the title only while this is a brand-new draft
            // whose slug hasn't been customized. Freeze it once the article is live (to protect
            // the public URL) or once the user has set a custom slug.
            const loadedSlug = src.slug || articleData.slug || '';
            const loadedTitle = src.title || '';
            const slugIsCustom = !!loadedSlug && loadedSlug !== generateSlug(loadedTitle);
            const alreadyLive = !articleData.uses_drafts || !!articleData.is_published_content;
            setSlugManuallyEdited(alreadyLive || slugIsCustom);
            setContent(src.content);
            setContentHtml(src.content_html || '');
            setContentText(src.content_text || '');
            setIcon(src.icon || '');
            setShowAuthor(articleData.settings?.showAuthor ?? true);
            setSeoTitle(articleData.settings?.seoTitle || '');
            setSeoDescription(articleData.settings?.seoDescription || '');
            setNoindex(!!articleData.settings?.noindex);
            setIsPublished(itemData.is_published); // item-level visibility ("Active")

            initialFormState.current = {
              title: src.title || '',
              description: src.description || '',
              slug: src.slug || articleData.slug || '',
              contentText: src.content_text || '',
              contentHtml: src.content_html || '',
              icon: src.icon || '',
              showAuthor: articleData.settings?.showAuthor ?? true,
              seoTitle: articleData.settings?.seoTitle || '',
              seoDescription: articleData.settings?.seoDescription || '',
              noindex: !!articleData.settings?.noindex,
              isPublished: itemData.is_published
            };
          }
        } else {
          setError(itemResponse.error || 'Failed to fetch item');
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to fetch data');
      } finally {
        setLoading(false);
        // Ensure navigation is unlocked after data loads (or fails to load)
        unlockNavigation();
      }
    };

    fetchData();
  }, [helpCenterIdParam, itemId, unlockNavigation, navigate]);


  const handleSave = async (silent = false): Promise<boolean> => {
    if (!item || !itemId) return false;

    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      const trimmedSlug = slug.trim();
      const originalSlug = article?.slug || '';
      
      // Always update through item endpoint
      const updateData: UpdateItemRequest = {
        is_published: isPublished,
        article_title: title,
        article_description: description,
        article_content: content,
        article_content_html: contentHtml,
        article_content_text: contentText,
        article_icon: icon || null,
        article_settings: {
          showAuthor,
          seoTitle: seoTitle.trim(),
          seoDescription: seoDescription.trim(),
          noindex,
        },
        // Only send slug if it was explicitly changed
        ...(trimmedSlug !== originalSlug && trimmedSlug ? { article_slug: trimmedSlug } : {}),
      };

      const response = await updateItem(helpCenterIdParam, itemId, updateData);
      
      if (!response.ok) {
        throw new Error(response.error || 'Failed to update item');
      }

      // Update local state with response
      if (response.item) {
        setItem(response.item);
        if (response.item.article) {
          const a: any = response.item.article;
          setArticle(a);
          setUsesDrafts(!!a.uses_drafts);
          setIsPublishedContent(!!a.is_published_content);
          setHasServerDraft(!!a.has_draft);
        }
      }

      // Update initial state after successful save
      initialFormState.current = {
        title,
        description,
        slug: trimmedSlug || originalSlug,
        contentText,
        contentHtml,
        icon,
        showAuthor,
        seoTitle,
        seoDescription,
        noindex,
        isPublished
      };

      // Unlock navigation after successful save
      unlockNavigation();

      if (!silent) toast.success(usesDrafts ? 'Draft saved' : 'Item saved successfully!');
      return true;
    } catch (err) {
      console.error('Error saving:', err);
      setError(err instanceof Error ? err.message : 'Failed to save');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Publish: for draft-mode articles, stage any unsaved edits then make the draft live.
  const handlePublish = async () => {
    if (!item || !itemId) return;
    try {
      setIsPublishing(true);
      setError(null);

      // Persist any unsaved edits as a draft first, so we publish what's in the editor.
      if (checkForChanges()) {
        const saved = await handleSave(true); // silent — the "Published" toast is enough
        if (!saved) return;
      }

      const response = await publishItem(helpCenterIdParam, itemId);
      if (!response.ok) {
        throw new Error(response.error || 'Failed to publish');
      }

      if (response.item?.article) {
        const a: any = response.item.article;
        setArticle(a);
        setUsesDrafts(!!a.uses_drafts);
        setIsPublishedContent(!!a.is_published_content);
        setHasServerDraft(!!a.has_draft);
      }
      unlockNavigation();
      toast.success('Published');
    } catch (err) {
      console.error('Error publishing:', err);
      setError(err instanceof Error ? err.message : 'Failed to publish');
      toast.error('Failed to publish');
    } finally {
      setIsPublishing(false);
    }
  };

  // Handle document import
  const handleImportDocument = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedExtensions = ['docx', 'html', 'md', 'markdown', 'txt'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      toast.error(`Unsupported file format. Allowed formats: ${allowedExtensions.join(', ')}`);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Validate file size (10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`File size exceeds maximum allowed size of ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setIsImporting(true);
    try {
      const response = await importDocument(file);
      
      if (response.ok && response.content) {
        // Update editor content with imported content
        // The TipTapEditor's useEffect will detect the content change and update the editor
        setContent(response.content);
        
        // If frontmatter metadata was extracted, populate article fields
        if (response.metadata) {
          if (response.metadata.title) {
            setTitle(response.metadata.title);
            // Auto-generate slug from title if slug wasn't provided in frontmatter
            if (!response.metadata.slug && !slugManuallyEdited) {
              setSlug(generateSlug(response.metadata.title));
            }
          }
          if (response.metadata.description) {
            setDescription(response.metadata.description);
          }
          if (response.metadata.icon) {
            setIcon(response.metadata.icon);
          }
          if (response.metadata.slug) {
            setSlug(response.metadata.slug);
            setSlugManuallyEdited(true);
          }
          // Note: tags are not directly settable in the current UI, but could be added later
        }
        
        toast.success('Document imported successfully! Review and edit as needed.');
      } else {
        toast.error(response.error || 'Failed to import document');
      }
    } catch (error) {
      console.error('Error importing document:', error);
      toast.error('Failed to import document');
    } finally {
      setIsImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Calculate word and character count
  const wordCount = contentText.trim() ? contentText.trim().split(/\s+/).length : 0;
  const characterCount = contentText.length;
  const readingTime = Math.max(1, Math.ceil(wordCount / 200)); // Assuming 200 words per minute
  const hasChanges = checkForChanges();
  const isEmpty = !contentText.trim();

  // Public URL of the article. The prefix (domain + category path) comes from the item's
  // place in the structure; the final segment is the live, editable slug.
  const structurePath = findItemPath(helpCenterStructure, itemId);
  const categoryPrefix = structurePath ? structurePath.replace(/[^/]+$/, '') : '/'; // drop last segment
  const urlPrefix = helpCenterInfo?.slug
    ? `${getHelpCenterBaseUrl(helpCenterInfo.slug)}${categoryPrefix}`
    : null;
  const publicUrl = urlPrefix ? `${urlPrefix}${(slug || '').trim()}` : null;

  const handleCopyUrl = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success('Public URL copied');
    } catch {
      toast.error('Could not copy URL');
    }
  };

  const handlePreview = async () => {
    if (!publicUrl) return;

    // Non-draft articles just open the live URL.
    if (!usesDrafts) {
      openExternalUrl(publicUrl);
      return;
    }

    const buildPreviewUrl = (token: string) => {
      const sep = publicUrl.includes('?') ? '&' : '?';
      return `${publicUrl}${sep}preview=${encodeURIComponent(token)}`;
    };

    // In the desktop iframe the parent opens external URLs, so awaiting first is fine.
    if (isInIframe) {
      try {
        const res = await getArticlePreviewToken(helpCenterIdParam, itemId);
        if (res.ok && res.token) {
          openExternalUrl(buildPreviewUrl(res.token));
          return;
        }
      } catch (e) {
        console.error('Preview token error:', e);
      }
      toast.error('Could not open preview');
      return;
    }

    // Standalone browser: open the tab synchronously (inside the click gesture) so it isn't
    // blocked as a popup, then point it at the preview URL once the token resolves.
    const win = window.open('', '_blank');
    try {
      const res = await getArticlePreviewToken(helpCenterIdParam, itemId);
      if (res.ok && res.token) {
        const url = buildPreviewUrl(res.token);
        if (win) win.location.href = url;
        else openExternalUrl(url);
        return;
      }
    } catch (e) {
      console.error('Preview token error:', e);
    }
    if (win) win.close();
    toast.error('Could not open preview');
  };

  // Load a past revision into the editor as unsaved changes (non-destructive).
  const handleRestoreRevision = (rev: ArticleRevisionFull) => {
    setTitle(rev.title || '');
    setDescription(rev.description || '');
    setIcon(rev.icon || '');
    setContent(rev.content);
    setContentHtml(rev.content_html || '');
    setContentText(rev.content_text || '');
  };

  if (loading) {
  return (
    <AppLayout showSidebar={true} breadcrumbs={breadcrumbs}>
      <div className="p-8 space-y-6">
        {/* Help Center Context Banner */}
        {/* {helpCenterInfo && (
          <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BookOpen className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <div>
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  This article will be added to "{helpCenterInfo.title}"
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">
                  {helpCenterInfo.categoryId 
                    ? 'It will be placed in the selected category'
                    : 'It will be added to the help center structure'}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              onClick={() => {
                // Remove help center context from URL
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('helpCenterId');
                newParams.delete('categoryId');
                navigate(`/articles/${articleId}/edit${newParams.toString() ? `?${newParams.toString()}` : ''}`, { replace: true });
                setHelpCenterInfo(null);
              }}
              title="Remove help center context"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )} */}
          <div className="flex justify-between items-center">
            <Skeleton className="h-8 w-32" />
            <div className="flex gap-2">
              <Skeleton className="h-10 w-20" />
              <Skeleton className="h-10 w-24" />
            </div>
          </div>
          <div className="space-y-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-96 w-full" />
          </div>
        </div>
      </AppLayout>
    );
  }

  const handleBack = () => {
    if (helpCenterInfo) {
      navigate(`/help-centers/${helpCenterInfo.id}/structure`);
    } else {
      navigate('/help-centers');
    }
  };

  return (
    <AppLayout showSidebar={true} breadcrumbs={breadcrumbs}>
      <div className="p-4">
        {/* Back Button */}
        <div className="mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleBack}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to {helpCenterInfo ? helpCenterInfo.title : 'Help Centers'}
          </Button>
        </div>

        
        {/* Status Messages */}
        {error && (
          <Card className="border-destructive mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-4 w-4" />
                <span>{error}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {success && (
          <Card className="border-green-200 bg-green-50 mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-4 w-4" />
                <span>{success}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Article header: icon, title, description, collapsible slug */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <div className="pt-1.5">
                    <IconPicker
                      value={icon || null}
                      onChange={(newIcon) => setIcon(newIcon || '')}
                    />
                  </div>
                  <div className="min-w-0 flex-1 space-y-1">
                    <Input
                      value={title}
                      onChange={(e) => {
                        const newTitle = e.target.value;
                        setTitle(newTitle);
                        // Only auto-generate slug if user hasn't manually edited it
                        if (!slugManuallyEdited) {
                          setSlug(generateSlug(newTitle));
                        }
                      }}
                      placeholder="Untitled article"
                      className="h-auto border-0 bg-transparent px-0 py-1 text-2xl md:text-3xl font-bold shadow-none focus-visible:ring-0 placeholder:text-muted-foreground/40"
                    />
                    <Textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Add a short description…"
                      className="min-h-0 h-auto resize-none border-0 bg-transparent px-0 py-1 text-base text-muted-foreground shadow-none focus-visible:ring-0 placeholder:text-muted-foreground/40"
                      rows={2}
                    />
                  </div>
                </div>

                {/* Public URL — full width, below the title block */}
                <div className="pt-4">
                  <Label htmlFor="slug" className="text-xs text-muted-foreground mb-1.5 block">
                    Public URL
                  </Label>
                  <div className="flex items-center gap-1.5">
                    {urlPrefix && (
                      <span
                        className="min-w-0 shrink truncate whitespace-nowrap font-mono text-xs text-muted-foreground"
                        title={urlPrefix}
                      >
                        {urlPrefix}
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <Input
                        id="slug"
                        value={slug}
                        onChange={(e) => {
                          setSlug(e.target.value);
                          setSlugManuallyEdited(true);
                        }}
                        placeholder="slug"
                        className="h-7 w-full font-mono text-xs"
                      />
                    </div>
                    {publicUrl && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 w-7 shrink-0 p-0"
                        onClick={handleCopyUrl}
                        title="Copy public URL"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Article Editor Card */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between gap-4 mb-3">
                  <p className="text-sm text-muted-foreground">
                    {isEmpty
                      ? 'Start writing, or press / for the command palette.'
                      : 'Press / for the command palette.'}
                  </p>
                  <div className="flex items-center gap-1 shrink-0">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".docx,.html,.md,.markdown,.txt"
                      onChange={handleImportDocument}
                      className="hidden"
                      disabled={isImporting}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isImporting}
                      className="gap-2 text-muted-foreground"
                    >
                      {isImporting ? (
                        <>
                          <RotateCcw className="h-4 w-4 animate-spin" />
                          Importing…
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4" />
                          Import
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                <TipTapEditor
                  helpCenterStructure={helpCenterStructure}
                  helpCenterSlug={helpCenterInfo?.slug}
                  content={content || {
                    type: 'doc',
                    content: [
                      {
                        type: 'paragraph',
                      },
                    ],
                  }}
                  onChange={(json, html, text) => {
                    setContent(json);
                    setContentHtml(html);
                    setContentText(text);
                  }}
                  placeholder="Start writing your article or press / to open the command palette"
                  editable={true}
                />
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Settings (sticky) */}
          <div className="space-y-4 lg:sticky lg:top-4 lg:self-start">
            {/* Actions */}
            <Card>
              <CardContent className="p-4 space-y-3">
                {usesDrafts ? (
                  <>
                    <div className="flex items-center gap-2 text-sm">
                      {!isPublishedContent ? (
                        <>
                          <span className="h-2 w-2 rounded-full bg-amber-500 shrink-0" />
                          <span className="text-muted-foreground">Draft — not published yet</span>
                        </>
                      ) : hasServerDraft || hasChanges ? (
                        <>
                          <span className="h-2 w-2 rounded-full bg-amber-500 shrink-0" />
                          <span className="text-muted-foreground">Published · unpublished changes</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-600 shrink-0" />
                          <span className="text-muted-foreground">Published</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={handlePublish}
                        className="flex-1"
                        size="lg"
                        disabled={isPublishing || (isPublishedContent && !hasServerDraft && !hasChanges)}
                      >
                        <Send className="h-4 w-4 mr-2" />
                        {isPublishing ? 'Publishing…' : isPublishedContent ? 'Publish changes' : 'Publish'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="lg"
                        className="w-11 shrink-0 p-0"
                        onClick={() => setShowPublishingHelp(true)}
                        title="How publishing works"
                        aria-label="How publishing works"
                      >
                        <HelpCircle className="h-4 w-4" />
                      </Button>
                    </div>
                    <Button
                      onClick={() => handleSave()}
                      variant="outline"
                      className="w-full"
                      size="sm"
                      disabled={!hasChanges}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Save draft
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 text-sm">
                      {hasChanges ? (
                        <>
                          <span className="h-2 w-2 rounded-full bg-amber-500 shrink-0" />
                          <span className="text-muted-foreground">Unsaved changes</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-600 shrink-0" />
                          <span className="text-muted-foreground">All changes saved</span>
                        </>
                      )}
                    </div>
                    <Button
                      onClick={() => handleSave()}
                      className="w-full"
                      size="lg"
                      disabled={!hasChanges}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </Button>
                    <Button
                      onClick={handleDiscardChanges}
                      variant="ghost"
                      className="w-full text-muted-foreground"
                      size="sm"
                      disabled={!hasChanges}
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Discard
                    </Button>
                  </>
                )}

                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full gap-2"
                        onClick={handlePreview}
                        disabled={!publicUrl}
                      >
                        <Eye className="h-4 w-4" />
                        Preview
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-[220px]">
                      {usesDrafts
                        ? 'Opens a private preview of your unpublished draft — only people with the link can see it.'
                        : 'Opens this page in the live help center.'}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </CardContent>
            </Card>

            {/* Settings: publish + author */}
            <Card>
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Eye className="h-4 w-4 shrink-0" />
                      <Label className="text-sm font-medium">Visible</Label>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Show this page in the help center
                    </p>
                  </div>
                  <Switch
                    checked={isPublished}
                    onCheckedChange={setIsPublished}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 shrink-0" />
                      <Label className="text-sm font-medium">Show Author</Label>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Display author information with the article
                    </p>
                  </div>
                  <Switch
                    checked={showAuthor}
                    onCheckedChange={setShowAuthor}
                  />
                </div>
              </CardContent>
            </Card>

            {/* SEO & social (optional, expandable) */}
            <Card className="border-dashed">
              <CardContent className="p-0">
                <button
                  type="button"
                  onClick={() => setShowSeo((v) => !v)}
                  className="flex w-full items-center justify-between gap-3 rounded-lg p-4 text-left transition-colors hover:bg-muted/60"
                  aria-expanded={showSeo}
                >
                  <span className="flex min-w-0 items-start gap-3">
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                      <Search className="h-4 w-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="flex items-center gap-2">
                        <span className="text-sm font-medium">SEO &amp; social</span>
                        {!isProCloudPlan && (
                          <Badge
                            variant="outline"
                            className="cursor-pointer border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50 px-1.5 py-0 text-[10px] text-amber-700 hover:from-amber-100 hover:to-yellow-100 dark:border-amber-700 dark:from-amber-950/20 dark:to-yellow-950/20 dark:text-amber-300"
                            onClick={(e) => {
                              e.stopPropagation();
                              openModal('upsell');
                            }}
                          >
                            <Sparkles className="mr-0.5 h-2.5 w-2.5" strokeWidth={2.5} />
                            Premium
                          </Badge>
                        )}
                      </span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        Customize the search title, description &amp; indexing
                      </span>
                    </span>
                  </span>
                  {showSeo ? (
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                </button>

                {showSeo && (
                  <div className="border-t px-4 pb-4 pt-4">
                    {!isProCloudPlan && (
                      <button
                        type="button"
                        onClick={() => openModal('upsell')}
                        className="mb-4 flex w-full items-center gap-2 rounded-md border border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50 px-3 py-2 text-left text-xs text-amber-800 transition hover:from-amber-100 hover:to-yellow-100 dark:border-amber-700 dark:from-amber-950/20 dark:to-yellow-950/20 dark:text-amber-300"
                      >
                        <Sparkles className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
                        <span>
                          Custom SEO is a Premium feature. Free articles use the title and
                          description automatically. <span className="font-semibold underline">Upgrade</span>
                        </span>
                      </button>
                    )}

                    <div className={!isProCloudPlan ? 'space-y-4 opacity-60' : 'space-y-4'}>
                      <div className="space-y-1.5">
                        <Label htmlFor="seo-title" className="text-xs font-medium">
                          SEO title
                        </Label>
                        <Input
                          id="seo-title"
                          value={seoTitle}
                          onChange={(e) => setSeoTitle(e.target.value)}
                          placeholder={title || 'Defaults to the article title'}
                          maxLength={70}
                          disabled={!isProCloudPlan}
                        />
                        <p className="text-[11px] text-muted-foreground">
                          {seoTitle
                            ? `${seoTitle.length}/70 characters`
                            : 'Used in search results and browser tabs. Falls back to the title.'}
                        </p>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="seo-description" className="text-xs font-medium">
                          Meta description
                        </Label>
                        <Textarea
                          id="seo-description"
                          value={seoDescription}
                          onChange={(e) => setSeoDescription(e.target.value)}
                          placeholder={description || 'Defaults to the article description'}
                          rows={3}
                          maxLength={160}
                          disabled={!isProCloudPlan}
                        />
                        <p className="text-[11px] text-muted-foreground">
                          {seoDescription
                            ? `${seoDescription.length}/160 characters`
                            : 'Shown under the title in search results. Falls back to the description.'}
                        </p>
                      </div>

                      <Separator />

                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <Label className="text-sm font-medium">Hide from search engines</Label>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Add a noindex tag so this article is not indexed
                          </p>
                        </div>
                        <Switch
                          checked={noindex}
                          onCheckedChange={setNoindex}
                          disabled={!isProCloudPlan}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Version history */}
            <Card>
              <CardContent className="p-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-2"
                  onClick={() => setShowHistory(true)}
                >
                  <History className="h-4 w-4" />
                  Version history
                </Button>
              </CardContent>
            </Card>

            {/* Statistics */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{wordCount} words</span>
                  <span>{characterCount} chars</span>
                  <span>{readingTime} min read</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <ArticleVersionHistory
        helpCenterId={helpCenterIdParam}
        itemId={itemId}
        open={showHistory}
        onOpenChange={setShowHistory}
        onRestore={handleRestoreRevision}
        currentTitle={title}
        currentDescription={description}
        currentHtml={contentHtml}
      />

      <PublishingHelpModal open={showPublishingHelp} onOpenChange={setShowPublishingHelp} />
    </AppLayout>
  );
};

export default EditArticlePage;